import React from 'react';
import { Plus, Trash2, Check, Sparkles } from 'lucide-react';

interface AddonFeaturesListProps {
  features: string[];
  onAddFeature: (text?: string) => void;
  onUpdateFeature: (index: number, val: string) => void;
  onRemoveFeature: (index: number) => void;
}

const COMMON_FEATURE_SUGGESTIONS = [
  '24/7 Dedicated Support Channel',
  'Sub-15 Minute SLA Response',
  'Custom SSL & Domain Mapping',
  'Automated Daily Cloud Backups',
  '365-Day Extended Audit Logs',
  'Dedicated Account Manager',
];

const AddonFeaturesList: React.FC<AddonFeaturesListProps> = ({
  features,
  onAddFeature,
  onUpdateFeature,
  onRemoveFeature,
}) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
          Add-on Features Included ({features.length})
        </label>
        <button
          type="button"
          onClick={() => onAddFeature('')}
          className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Feature
        </button>
      </div>

      <div className="space-y-2">
        {features.map((feature, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
              <Check className="w-3.5 h-3.5" />
            </div>
            <input
              type="text"
              placeholder={`Feature or benefit ${idx + 1}`}
              value={feature}
              onChange={e => onUpdateFeature(idx, e.target.value)}
              className="flex-1 px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {features.length > 1 && (
              <button
                type="button"
                onClick={() => onRemoveFeature(idx)}
                className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-white dark:hover:bg-slate-800 transition-colors"
                title="Remove feature"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        {features.length === 0 && (
          <button
            type="button"
            onClick={() => onAddFeature('')}
            className="w-full py-3 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50/50 flex items-center justify-center gap-1.5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add First Feature
          </button>
        )}
      </div>

      {/* Feature Presets */}
      <div className="pt-1.5">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-indigo-500" /> Click to add common benefits:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {COMMON_FEATURE_SUGGESTIONS.map(sug => {
            if (features.includes(sug)) return null;
            return (
              <button
                key={sug}
                type="button"
                onClick={() => onAddFeature(sug)}
                className="text-[11px] px-2.5 py-1 rounded-lg bg-white dark:bg-slate-800 border border-indigo-100 dark:border-indigo-800/60 hover:border-indigo-400 text-slate-700 dark:text-slate-200 font-medium transition-colors"
              >
                + {sug}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AddonFeaturesList;
